import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Fullscreen pro libovolný element — jedna implementace pro celou app
 * (PartialWrapper i spotlight kamery).
 *
 * KLÍČOVÉ ROZHODNUTÍ: jediný zdroj pravdy je vlastní React state `active`, ne
 * `document.fullscreenElement`. Nativní API je jen bonus.
 *
 * Proč: iPhone Safari NEUMÍ element fullscreen (jen `<video>.webkitEnterFullscreen`),
 * takže `requestFullscreen` i `webkitRequestFullscreen` jsou `undefined` a
 * `document.fullscreenElement` zůstane `null`. Původní implementace v PartialWrapperu
 * se rozhodovala právě podle něj (`if (!document.fullscreenElement) enter() else exit()`),
 * takže na mobilu vedla KAŽDÁ kliknutí do enter větve — fullscreen se nedal zavřít.
 *
 * Tady CSS „pseudo-fullscreen" (`active`) funguje vždy a nativní FS se jen zkusí
 * navrch. Když projde, `native === true` a má smysl zamykat orientaci; když ne,
 * uživatel to nepozná (jen mu zůstane viditelný adresní řádek).
 */

type OrientationLockType = "any" | "natural" | "landscape" | "portrait" | "landscape-primary" | "portrait-primary";

type FsElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
  msRequestFullscreen?: () => Promise<void> | void;
};
type FsDocument = Document & {
  webkitExitFullscreen?: () => Promise<void> | void;
  msExitFullscreen?: () => Promise<void> | void;
  webkitFullscreenElement?: Element | null;
};
type LockableOrientation = ScreenOrientation & {
  lock?: (orientation: OrientationLockType) => Promise<void>;
  unlock?: () => void;
};

export interface FullscreenApi {
  /** Jediný zdroj pravdy pro CSS overlay. */
  active: boolean;
  /** Nativní Fullscreen API se skutečně podařilo (→ smysl orientation locku). */
  native: boolean;
  enter: () => void;
  exit: () => void;
  toggle: () => void;
}

interface Options {
  /** Zamknout landscape (jen když projde nativní FS — mimo něj lock vždy hodí). */
  lockLandscape?: boolean;
  /** Zamknout scroll dokumentu po dobu fullscreenu. */
  lockScroll?: boolean;
  /** Zavolá se při ukončení (i nativním — Esc, Android back). */
  onExit?: () => void;
  /**
   * Zpracovat Esc v capture fázi a zastavit ho.
   *
   * Pro vnořený fullscreen (spotlight kamery uvnitř fullscreenu wrapperu): bez toho
   * by Esc zavřel OBOJE — oba hooky mají listener na `window` a ten dřív
   * zaregistrovaný (vnější wrapper) by se spustil taky. Capture fáze proběhne před
   * bubbling listenery, takže vnitřní vrstva Esc spotřebuje pro sebe.
   */
  escCapture?: boolean;
}

const currentFsElement = (): Element | null => {
  const doc = document as FsDocument;
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
};

export function useFullscreen(
  ref: React.RefObject<HTMLElement | null>,
  { lockLandscape = false, lockScroll = true, onExit, escCapture = false }: Options = {}
): FullscreenApi {
  const [active, setActive] = useState(false);
  const [native, setNative] = useState(false);
  /** True jen mezi naším requestem a jeho ukončením — ať nereagujeme na cizí FS. */
  const ownsNative = useRef(false);
  // Callback se drží v refu, aby ho listenery viděly aktuální bez re-registrace.
  // Zápis patří do efektu, ne do renderu (React 19 compiler pravidlo).
  const onExitRef = useRef(onExit);
  useEffect(() => {
    onExitRef.current = onExit;
  }, [onExit]);

  const releaseNative = useCallback(() => {
    ownsNative.current = false;
    setNative(false);
    const doc = document as FsDocument;
    if (currentFsElement()) {
      void (doc.exitFullscreen?.() ?? doc.webkitExitFullscreen?.() ?? doc.msExitFullscreen?.());
    }
    const orientation = screen.orientation as LockableOrientation | undefined;
    orientation?.unlock?.();
  }, []);

  const enter = useCallback(() => {
    // CSS overlay naskočí okamžitě — nezávisle na tom, co udělá nativní API.
    setActive(true);

    const el = ref.current as FsElement | null;
    if (!el) return;
    const request = el.requestFullscreen ?? el.webkitRequestFullscreen ?? el.msRequestFullscreen;
    if (!request) return; // iPhone Safari — zůstane pseudo-fullscreen, což je v pořádku

    void Promise.resolve(request.call(el))
      .then(() => {
        ownsNative.current = true;
        setNative(true);
        if (!lockLandscape) return;
        const orientation = screen.orientation as LockableOrientation | undefined;
        // Lock smí selhat (desktop, iPad, odepřené oprávnění) — není to chyba.
        return orientation?.lock?.("landscape").catch(() => undefined);
      })
      .catch(() => {
        // Nativní FS odmítnut (např. gesture requirement) — pseudo-fullscreen stačí.
        ownsNative.current = false;
        setNative(false);
      });
  }, [lockLandscape, ref]);

  const exit = useCallback(() => {
    // Bezpodmínečně — tohle je oprava nedosažitelné exit větve.
    setActive(false);
    releaseNative();
    onExitRef.current?.();
  }, [releaseNative]);

  const toggle = useCallback(() => {
    if (active) exit();
    else enter();
  }, [active, enter, exit]);

  // Sync s nativním ukončením (Esc, Android back, systémové gesto).
  useEffect(() => {
    const onChange = () => {
      if (!ownsNative.current) return;
      if (!currentFsElement()) {
        ownsNative.current = false;
        setNative(false);
        setActive(false);
        onExitRef.current?.();
      }
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);

  // Esc pro pseudo-fullscreen (nativní FS si Esc řeší sám a spustí fullscreenchange).
  useEffect(() => {
    if (!active || native) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // `stopImmediatePropagation` zastaví i listenery v dalších fázích — jen tak
      // vnořený spotlight nezavře zároveň fullscreen wrapperu pod sebou.
      if (escCapture) e.stopImmediatePropagation();
      else e.stopPropagation();
      exit();
    };
    window.addEventListener("keydown", onKey, { capture: escCapture });
    return () => window.removeEventListener("keydown", onKey, { capture: escCapture });
  }, [active, native, exit, escCapture]);

  // Scroll lock. Obnovuje se PŮVODNÍ hodnota, ne prázdný string — jinak by vnořený
  // případ (spotlight uvnitř fullscreenu wrapperu) po zavření uvolnil scroll oběma.
  useEffect(() => {
    if (!active || !lockScroll) return;
    const el = document.documentElement;
    const prev = el.style.overflow;
    el.style.overflow = "hidden";
    return () => {
      el.style.overflow = prev;
    };
  }, [active, lockScroll]);

  // Unmount během fullscreenu (např. sbalení wrapperu) nesmí nechat viset nativní FS.
  useEffect(
    () => () => {
      if (ownsNative.current) releaseNative();
    },
    [releaseNative]
  );

  return { active, native, enter, exit, toggle };
}
